import { Grid, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'

import {
  fetchApartmentDashboard,
  fetchCoordinatorDashboard
} from 'api/apartment'
import { Loading } from 'app/components/generic/LottieComponents'
import { formatAmount } from 'app/utils'

import ApartmentPageHeader from '../shared/ApartmentPageHeader'
import { useSelectedApartment } from '../shared/apartmentStore'
import MonthFilter from '../shared/MonthFilter'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  metricCard: {
    padding: theme.spacing(2.5),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  metricValue: { fontWeight: 600, marginTop: theme.spacing(0.5) },
  sectionTitle: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1.5),
    fontWeight: 600
  },
  panel: { padding: theme.spacing(2.5) },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  empty: { padding: theme.spacing(2.5), color: theme.palette.text.secondary },
  unpaid: { color: theme.palette.warning.main, fontWeight: 600 },
  paid: { color: theme.palette.success.main, fontWeight: 600 }
}))

const Metric = ({ label, value, classes }) => (
  <Paper variant="outlined" className={classes.metricCard}>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="h4" className={classes.metricValue}>
      {value}
    </Typography>
  </Paper>
)

const ApartmentDashboard = () => {
  const classes = useStyles()
  const [apartment] = useSelectedApartment()
  const [data, setData] = React.useState(null)
  const [tasks, setTasks] = React.useState(null)
  // '' = all time. Revenue by month/programme narrows to the chosen month.
  const [month, setMonth] = React.useState('')

  React.useEffect(() => {
    let active = true
    setData(null)
    setTasks(null)
    // WITHOUT a .catch this page spins forever: a rejected fetch never calls
    // setData, `data` stays null, and the component returns <Loading/> for good.
    // That is the "keeps loading for a really long time" — it was never slow,
    // it was a request that failed and nothing ever handled it.
    fetchApartmentDashboard(apartment.id, month)
      .then((res) => active && setData(res))
      .catch(
        () =>
          active &&
          setData({ revenueByMonth: [], revenueByProgram: [], attendance: {} })
      )
    fetchCoordinatorDashboard(apartment.id)
      .then((res) => active && setTasks(res.tasks))
      .catch(() => active && setTasks(null))
    return () => {
      active = false
    }
  }, [apartment.id, month])

  if (!data) return <Loading />

  const metrics = [
    { label: 'Active Enrolments', value: data.activeEnrolments },
    // Same vocab as the Overview: we say 'parents', not 'families'.
    { label: 'Active parents', value: data.activeFamilies },
    { label: 'Trials This Month', value: data.trialsThisMonth },
    { label: 'Open Issues', value: data.openIssues }
  ]

  const taskCards = tasks
    ? [
        { label: 'Unpaid parents', value: tasks.unpaidFamilies },
        // Inventory + purchase requests removed from the apartment UI (2026-07);
        // the backend still aggregates them.
        { label: 'Open incidents', value: tasks.openIncidents },
        { label: 'Trial follow-ups', value: tasks.trialFollowups }
      ]
    : []

  return (
    <div className={classes.root}>
      <ApartmentPageHeader
        title="Overview"
        action={
          <MonthFilter
            value={month}
            onChange={setMonth}
            allowAll
            apartmentId={apartment.id}
          />
        }
      />

      {taskCards.length > 0 && (
        <>
          <Typography variant="h6" className={classes.sectionTitle}>
            My Tasks
          </Typography>
          <Grid container spacing={2}>
            {taskCards.map((m) => (
              <Grid item xs={6} md={2} key={m.label}>
                <Metric label={m.label} value={m.value} classes={classes} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <Typography variant="h6" className={classes.sectionTitle}>
        Overview
      </Typography>
      <Grid container spacing={2}>
        {metrics.map((m) => (
          <Grid item xs={6} md={3} key={m.label}>
            <Metric label={m.label} value={m.value} classes={classes} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Revenue by month
          </Typography>
          <Paper variant="outlined" className={classes.panel}>
            {data.revenueByMonth.length === 0 ? (
              <span className={classes.empty}>No revenue yet</span>
            ) : (
              data.revenueByMonth.map((m) => (
                <div className={classes.row} key={m.month}>
                  <span>{m.month}</span>
                  <span>{formatAmount(m.revenue)}</span>
                  <span>Apartment 15%: {formatAmount(m.apartmentShare)}</span>
                  <span className={m.paid ? classes.paid : classes.unpaid}>
                    {m.paid ? 'Paid out' : 'Pending'}
                  </span>
                </div>
              ))
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Revenue by program
          </Typography>
          <Paper variant="outlined" className={classes.panel}>
            {data.revenueByProgram.length === 0 ? (
              <span className={classes.empty}>No enrolments yet</span>
            ) : (
              data.revenueByProgram.map((p) => (
                <div className={classes.row} key={p.program}>
                  <span>{p.program}</span>
                  <span>{p.enrolments} enrolments</span>
                  <span>{formatAmount(p.revenue)}</span>
                </div>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      <Typography variant="h6" className={classes.sectionTitle}>
        Attendance snapshot
      </Typography>
      <Paper variant="outlined" className={classes.panel}>
        <div className={classes.row}>
          <span>Present</span>
          <span>{data.attendance.present}</span>
        </div>
        <div className={classes.row}>
          <span>Absent</span>
          <span>{data.attendance.absent}</span>
        </div>
        <div className={classes.row}>
          <span>Holiday</span>
          <span>{data.attendance.holiday}</span>
        </div>
      </Paper>
    </div>
  )
}

export default ApartmentDashboard
